import React, { useEffect, useState } from "react";
import { FaImage, FaUpload } from "react-icons/fa";
import toast from "react-hot-toast";
import {
  TeamFormSecondaryButton,
  TeamFormPrimaryButton,
  TeamFormContentStackTertiary,
  TeamFormEmptyStatePanel,
  TeamFormActionRow,
  TeamFormGridLayout,
  TeamFormContentStack,
  TeamFormGridLayoutSecondary,
  TeamFormContentStackSecondary,
  TeamFormHeaderRow,
  TeamFormPreviewImage,
  TeamFormFormStack,
  TeamFormFieldInputSecondary,
  TeamFormFieldInput,
  TeamFormFieldLabelSecondary,
  TeamFormFieldLabel,
  TeamFormMetaText,
  TeamFormFieldSelect,
  TeamFormInlineText,
} from "components/molecules/forms/team-form/styles/team-form.inline.styles";
type TeamImageValue = string | File | null | undefined;
interface TeamFormValues {
  name: string;
  sport: string;
  country: string;
  status: string;
  level: string;
  image?: TeamImageValue;
}
interface TeamFormProps {
  initialValues: TeamFormValues;
  onSubmit: (values: TeamFormValues) => Promise<void> | void;
  onCancel?: () => void;
  sports?: {
    label: string;
    value: string;
  }[];
  countries?: {
    label: string;
    value: string;
  }[];
}
const resolveImagePreview = (value?: TeamImageValue) => (typeof value === "string" ? value : "");
export const TeamForm: React.FC<TeamFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  sports = [],
  countries = [],
}) => {
  const [values, setValues] = useState<TeamFormValues>(initialValues);
  const [previewUrl, setPreviewUrl] = useState(resolveImagePreview(initialValues.image));
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    setValues(initialValues);
    setPreviewUrl(resolveImagePreview(initialValues.image));
  }, [initialValues]);
  const updateValue = (key: keyof TeamFormValues, value: string | File) => {
    setValues((current) => ({
      ...current,
      [key]: value,
    }));
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!values.name?.trim() || !values.sport?.trim() || !values.country?.trim()) {
      toast.error("Name, sport, and country are required.");
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...values,
        name: values.name.trim(),
        sport: values.sport.trim(),
        country: values.country.trim(),
        level: values.level?.trim() || "",
        status: values.status || "active",
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Unable to save this team.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <TeamFormFormStack onSubmit={handleSubmit}>
      <TeamFormGridLayout>
        <TeamFormContentStack>
          <TeamFormFieldLabel>Team name</TeamFormFieldLabel>
          <TeamFormFieldInput
            onChange={(event) => updateValue("name", event.target.value)}
            required
            value={values.name || ""}
          />
        </TeamFormContentStack>

        <TeamFormGridLayoutSecondary>
          <TeamFormContentStack>
            <TeamFormFieldLabel>Sport</TeamFormFieldLabel>
            <TeamFormFieldSelect
              onChange={(event) => updateValue("sport", event.target.value)}
              value={values.sport || ""}
            >
              <option value="">Select sport</option>
              {sports.map((sport) => (
                <option key={sport.value} value={sport.value}>
                  {sport.label}
                </option>
              ))}
            </TeamFormFieldSelect>
          </TeamFormContentStack>

          <TeamFormContentStack>
            <TeamFormFieldLabel>Country</TeamFormFieldLabel>
            <TeamFormFieldSelect
              onChange={(event) => updateValue("country", event.target.value)}
              value={values.country || ""}
            >
              <option value="">Select country</option>
              {countries.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </TeamFormFieldSelect>
          </TeamFormContentStack>

          <TeamFormContentStack>
            <TeamFormFieldLabel>Level</TeamFormFieldLabel>
            <TeamFormFieldSelect
              onChange={(event) => updateValue("level", event.target.value)}
              value={values.level || ""}
            >
              <option value="">Select level</option>
              <option value="Local">Local</option>
              <option value="Regional">Regional</option>
              <option value="Provincial">Provincial</option>
              <option value="National">National</option>
              <option value="International">International</option>
              <option value="Club">Club</option>
              <option value="Academy">Academy</option>
              <option value="University">University</option>
              <option value="Professional">Professional</option>
              <option value="Amateur">Amateur</option>
            </TeamFormFieldSelect>
          </TeamFormContentStack>

          <TeamFormContentStack>
            <TeamFormFieldLabel>Status</TeamFormFieldLabel>
            <TeamFormFieldSelect
              onChange={(event) => updateValue("status", event.target.value)}
              value={values.status || "active"}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="retired">Retired</option>
            </TeamFormFieldSelect>
          </TeamFormContentStack>
        </TeamFormGridLayoutSecondary>
        <TeamFormContentStackSecondary>
          <TeamFormHeaderRow>
            <div>
              <TeamFormFieldLabel>Team image</TeamFormFieldLabel>
              <TeamFormMetaText>
                Upload a new team image or keep using a hosted URL.
              </TeamFormMetaText>
            </div>
            <TeamFormFieldLabelSecondary>
              <FaUpload size={12} />
              Choose file
              <TeamFormFieldInputSecondary
                accept="image/*"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  updateValue("image", file);
                  setPreviewUrl(URL.createObjectURL(file));
                }}
                type="file"
              />
            </TeamFormFieldLabelSecondary>
          </TeamFormHeaderRow>

          <TeamFormContentStackTertiary>
            {previewUrl ? (
              <TeamFormPreviewImage
                alt="Team preview"
                fallbackLabel={values.name || "Team preview"}
                src={previewUrl}
                variant="team"
              />
            ) : (
              <TeamFormEmptyStatePanel>
                <TeamFormInlineText>
                  <FaImage size={14} />
                  No image selected yet
                </TeamFormInlineText>
              </TeamFormEmptyStatePanel>
            )}

            <TeamFormFieldInput
              onChange={(event) => {
                updateValue("image", event.target.value);
                setPreviewUrl(event.target.value);
              }}
              placeholder="https://example.com/team-image.jpg"
              value={typeof values.image === "string" ? values.image : ""}
            />
          </TeamFormContentStackTertiary>
        </TeamFormContentStackSecondary>
      </TeamFormGridLayout>

      <TeamFormActionRow>
        <TeamFormSecondaryButton onClick={onCancel} type="button">
          Cancel
        </TeamFormSecondaryButton>
        <TeamFormPrimaryButton disabled={isSubmitting} type="submit">
          {isSubmitting ? "Saving..." : initialValues?.name ? "Save changes" : "Add team"}
        </TeamFormPrimaryButton>
      </TeamFormActionRow>
    </TeamFormFormStack>
  );
};
